import { setDefaultOptions, type Locale } from 'date-fns';
import i18n, {
  keyFromSelector as baseKeyFromSelector,
  type DefaultNamespace,
  type FlatNamespace,
  type GetSource,
  type SelectorFn,
} from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import prettyBytes from 'pretty-bytes';
import { initReactI18next } from 'react-i18next';
import * as z from 'zod';
import type common from '../locales/pt/common.json';
import type privateLocale from '../locales/pt/private.json';
import type publicLocale from '../locales/pt/public.json';
import { errorMap } from './validation';

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'common';
    enableSelector: true;
    resources: {
      common: typeof common;
      public: typeof publicLocale;
      private: typeof privateLocale;
    };
  }
}

export const supportedLanguages = {
  pt: 'Português',
  es: 'Español',
};

export type SupportedLanguages = keyof typeof supportedLanguages;

export const defaultLanguage: SupportedLanguages = 'pt';

export const supportedLanguageKeys = Object.keys(
  supportedLanguages,
) as SupportedLanguages[];

const localeMapping: Record<SupportedLanguages, () => Promise<Locale>> = {
  pt: () => import('date-fns/locale/pt-BR').then(x => x.ptBR),
  es: () => import('date-fns/locale/es').then(x => x.es),
};

const genericLocaleToSpecificLocaleMapping: Partial<
  Record<SupportedLanguages, string>
> = {
  pt: 'pt-br',
};

async function configureLocale(lng: SupportedLanguages) {
  const key = lng in localeMapping ? lng : supportedLanguageKeys[0];
  const importLocales = localeMapping[key as keyof typeof localeMapping];

  const locale = await importLocales();

  setDefaultOptions({ locale });
}

z.config({
  localeError: errorMap,
});

let instance = i18n.use(LanguageDetector).use(initReactI18next);

if (import.meta.env.VITE_ENV !== 'testing') {
  instance = instance.use(
    resourcesToBackend(
      (lng: string, ns: string) => import(`../locales/${lng}/${ns}.json`),
    ),
  );
}

instance.on('languageChanged', async lng => {
  const supportedLng = lng as SupportedLanguages;

  const specificLocale =
    genericLocaleToSpecificLocaleMapping[supportedLng] ?? lng;

  document.documentElement.lang = specificLocale;
  document.documentElement.dir = i18n.dir(specificLocale);

  await configureLocale(supportedLng);
});

/**
 * Exportado em uma promise para que consigamos aguardar na inicialização da aplicação,
 * caso contrário algumas requisições podem falhar por não ter o idioma principal ainda carregado.
 */
export const i18nInitPromise = instance.init({
  fallbackLng: defaultLanguage,
  supportedLngs: supportedLanguageKeys,
  defaultNS: 'common',
  fallbackNS: 'common',
  ns: 'common',
  load: 'languageOnly',
  partialBundledLanguages: true,
  debug: false,
});

instance.services.formatter?.add(
  'fileSize',
  (value: number, lng: string | undefined) => {
    return prettyBytes(value, { locale: lng, maximumFractionDigits: 0 });
  },
);

export type Selector<Ns extends FlatNamespace> = SelectorFn<
  GetSource<Ns, undefined>,
  string,
  { ns: Ns }
>;

function getKeyFromSelector<Ns extends FlatNamespace = DefaultNamespace>(
  selector: Selector<Ns>,
  { ns }: { ns?: Ns } = {},
) {
  const result = baseKeyFromSelector(selector);

  return ns && ns !== 'common' ? `${ns}:${result}` : result;
}

export default i18n;
export { getKeyFromSelector, instance as i18nInstance };

// Aparentemente o vitest possui algum bug que faz mocks de módulos do npm não funcionarem no modo browser,
// então reexportamos aqui para que seja considerado um módulo local
// https://github.com/vitest-dev/vitest/issues/6034
// https://github.com/vitest-dev/vitest/issues/3046
export * from 'react-i18next';
